import { useEffect, useState } from "react";
import { KeyRound, ShieldCheck, Trash2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { useProviderVault } from "@/context/ProviderContext";
import { useWallet } from "@/context/WalletContext";
import { DEFAULT_PROVIDER } from "@/types";

export function ProviderDialog({ open, onOpenChange }: { open: boolean; onOpenChange: (open: boolean) => void }) {
  const { address } = useWallet();
  const { secret, save, remove, busy } = useProviderVault();
  const [apiKey, setApiKey] = useState("");
  const [baseUrl, setBaseUrl] = useState(DEFAULT_PROVIDER.baseUrl);
  const [model, setModel] = useState(DEFAULT_PROVIDER.model);
  const [useProxy, setUseProxy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!secret) return;
    setApiKey(secret.apiKey);
    setBaseUrl(secret.config.baseUrl);
    setModel(secret.config.model);
    setUseProxy(secret.config.useProxy);
  }, [secret]);

  const handleSave = async () => {
    setError(null);
    if (!address) {
      setError("Connect your wallet before saving a key.");
      return;
    }
    if (!apiKey.trim() || !baseUrl.trim() || !model.trim()) {
      setError("API key, base URL, and model are required.");
      return;
    }
    try {
      await save({
        apiKey: apiKey.trim(),
        config: { baseUrl: baseUrl.trim(), model: model.trim(), useProxy },
      });
      onOpenChange(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : "Could not save the key.");
    }
  };

  return (
    <Dialog onOpenChange={onOpenChange} open={open}>
      <DialogContent className="border-white/10 bg-[#12141e] text-white sm:max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-xl"><KeyRound className="size-5 text-cyan-300" />Your AI connection</DialogTitle>
          <DialogDescription className="text-white/55">The key is encrypted on this device. A wallet signature unlocks it; AniSoul never stores it in Cloudflare.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <label className="block space-y-2 text-sm">
            <span className="text-white/70">API key</span>
            <Input autoComplete="off" className="border-white/10 bg-white/5" onChange={(event) => setApiKey(event.target.value)} placeholder="sk-or-v1-…" type="password" value={apiKey} />
          </label>
          <label className="block space-y-2 text-sm">
            <span className="text-white/70">OpenAI-compatible base URL</span>
            <Input className="border-white/10 bg-white/5 font-mono text-xs" onChange={(event) => setBaseUrl(event.target.value)} value={baseUrl} />
          </label>
          <label className="block space-y-2 text-sm">
            <span className="text-white/70">Model</span>
            <Input className="border-white/10 bg-white/5 font-mono text-xs" onChange={(event) => setModel(event.target.value)} value={model} />
          </label>
          <label className="flex cursor-pointer items-start gap-3 rounded-xl border border-white/8 bg-white/[.025] p-3 text-sm">
            <input checked={useProxy} className="mt-1 accent-cyan-300" disabled={!import.meta.env.VITE_AI_PROXY_URL} onChange={(event) => setUseProxy(event.target.checked)} type="checkbox" />
            <span><span className="block text-white/80">Use stateless Worker proxy</span><span className="text-xs text-white/45">Only needed when a provider blocks browser requests. No request data is stored.</span></span>
          </label>
          <div className="flex items-center gap-2 rounded-xl border border-emerald-300/15 bg-emerald-300/6 p-3 text-xs text-emerald-100/75"><ShieldCheck className="size-4 shrink-0" />AES-GCM encrypted in IndexedDB. Plain key exists only in this tab after unlock.</div>
          {error && <p className="text-sm text-red-300">{error}</p>}
          <div className="flex justify-between gap-3">
            <Button className="text-red-200 hover:bg-red-400/10" disabled={!secret || busy} onClick={() => void remove()} variant="ghost"><Trash2 className="size-4" />Delete local key</Button>
            <Button className="bg-cyan-300 text-[#06100f] hover:bg-cyan-200" disabled={busy} onClick={() => void handleSave()}>{busy ? "Waiting for wallet" : "Encrypt and save"}</Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
